import React from 'react';
import { TTextTranslationNamespaces } from 'types';
import { InputProps } from 'antd/lib/input/Input';
import { TOptions } from 'i18next';
import { useAdditionalValidation } from 'components/WithAdditionalValidation/useAdditionalValidation';
import { Input, InputNumber } from 'antd';
import { useTranslation } from 'react-i18next';
import { Icon, IconName } from 'components/Icon/Icon';
import Text from 'components/Text/Text';
import classNames from 'classnames';
import style from './OutsideFormInputNew.module.scss';

type TDefaultInputProps = Omit<InputProps, 'onChange'>;

interface IProps extends TDefaultInputProps {
	name: string;
	label: string;
	value?: string;
	numberValue?: Nullable<number>;
	errorMessage?: string;
	blockId?: string;
	placeholder?: string;
	className?: string;
	containerClassName?: string;
	labelClassName?: string;
	translationNameSpace?: TTextTranslationNamespaces;
	testId?: string;
	type?: 'text' | 'password' | 'textArea' | 'number';
	withValidation?: boolean;
	translationOptions?: TOptions | string;
	min?: number;
	max?: number;
	precision?: number;
	onChange?: (value: string) => void;
	onChangeNumber?: (value: number | null) => void;
}

export const OutsideFormInputNew: React.FC<IProps> = React.memo(
	({
		name,
		label,
		value,
		numberValue,
		errorMessage = 'commonError',
		blockId,
		placeholder = 'inputPlaceholder',
		className,
		containerClassName,
		labelClassName,
		translationNameSpace = 'form',
		testId,
		type = 'text',
		withValidation = true,
		translationOptions,
		min,
		max,
		precision,
		disabled = false,
		onChange,
		onChangeNumber,
		...restProps
	}) => {
		const { isShowValidationError, validationErrorMessage } = useAdditionalValidation(
			value,
			errorMessage,
			name,
			blockId,
		);
		const { t } = useTranslation(translationNameSpace);

		const isError = withValidation && isShowValidationError;

		const containerClasses = classNames(style.container, containerClassName);
		const inputClasses = classNames(style.input, { [style.inputError]: isError }, className);
		const textAreaClasses = classNames(style.textArea, { [style.inputError]: isError }, className);
		const inputNumberClasses = classNames(style.inputNumber, className);

		const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			onChange?.(e.target.value);
		};

		const handleChangeTextArea = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
			onChange?.(e.target.value);
		};

		const handleChangeInputNumber = (e: Nullable<number>) => {
			onChangeNumber?.(e);
		};

		const renderInputElement = () => {
			switch (type) {
				case 'text':
					return (
						<Input
							className={inputClasses}
							value={value}
							onChange={handleChange}
							allowClear={true}
							placeholder={t(placeholder)}
							disabled={disabled}
							{...restProps}
						/>
					);
				case 'password':
					return (
						<Input.Password
							className={inputClasses}
							value={value}
							onChange={handleChange}
							placeholder={t(placeholder)}
							disabled={disabled}
							iconRender={(visible) => <Icon name={visible ? IconName.eye : IconName.eyeOff} size="lg" />}
							{...restProps}
						/>
					);
				case 'textArea':
					return (
						<Input.TextArea
							className={textAreaClasses}
							value={value}
							onChange={handleChangeTextArea}
							allowClear={true}
							autoSize={true}
							placeholder={t(placeholder)}
							disabled={disabled}
						/>
					);
				case 'number':
					return (
						<InputNumber
							className={inputNumberClasses}
							value={typeof numberValue === 'number' ? numberValue : undefined}
							onChange={handleChangeInputNumber}
							placeholder={t(placeholder)}
							min={min}
							max={max}
							disabled={disabled}
							precision={precision}
						/>
					);
				default:
					return (
						<Input
							className={inputClasses}
							value={value}
							onChange={handleChange}
							allowClear={true}
							placeholder={t(placeholder)}
							disabled={disabled}
							{...restProps}
						/>
					);
			}
		};

		return (
			<div data-test={testId} className={containerClasses}>
				<div className={style.labelRow}>
					<Text
						className={labelClassName}
						translationNameSpace={translationNameSpace}
						translationKey={label}
						translationOptions={translationOptions}
						size="xs"
						theme="neutralDark"
						display="inline"
					/>
				</div>
				{renderInputElement()}
				{isError && (
					<div className={style.errorMessage}>
						<Text
							translationNameSpace="error"
							translationKey={validationErrorMessage}
							theme="error"
							size="xs"
							display="inline"
						/>
					</div>
				)}
			</div>
		);
	},
);
